/**
 * Edits the Greenhouse job sync scope from the combined setup dialog and settings page.
 *
 * 1. Read the typed organization preference and parse it into a draft.
 * 2. Fetch only the remote list for the active restricted strategy.
 * 3. Save one GraphQL input field and close only after a successful response.
 *
 * The preference parser is in jobSyncScope.ts. The Greenhouse setup dialog is the caller of this
 * shared panel.
 */

import React, { FC, useCallback, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FilterSelect, FilterSelectGroup, MaybeTooltip, defaultRenderGroup } from '@modernloop/shared/components';
import { useFeatureFlagBool } from '@modernloop/shared/feature-flag';
import { ResetIcon } from '@modernloop/shared/icons';
import {
  Alert,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  ListItem,
  ListItemButton,
  Radio,
  RadioGroup,
  Stack,
  Skeleton,
  Tooltip,
  Button,
  Typography,
} from '@mui/material';
import { uniqBy } from 'lodash';
import { useSnackbar } from 'notistack';

import {
  AtsJobSyncScopePref,
  AtsRemoteDepartment,
  AtsRemoteJob,
  AtsRemoteOffice,
  JsonOrgPref,
  useAtsJobSyncScopeUpdateMutation,
  useAtsRemoteDepartmentsQuery,
  useAtsRemoteJobsQuery,
  useAtsRemoteOfficesQuery,
} from 'src/generated/mloop-graphql';

import { useOrgPrefJsonTyped } from 'src/hooks/api/org';

import {
  buildJobSyncScopeTree,
  filterJobSyncScopeTree,
  JobSyncScopeDraft,
  JobSyncScopeTreeOption,
  parseJobSyncScopeDraft,
  toggleJobSyncScopeSelection,
} from './jobSyncScope';

/** GraphQL operations used to load Greenhouse scope options and save the selected scope. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GreenhouseJobSyncScopeGql = gql`
  query AtsRemoteDepartments($input: AtsRemoteListsInput) {
    atsRemoteDepartments(input: $input) {
      __typename
      ... on AtsRemoteDepartmentsSuccess {
        items {
          id
          name
          parentId
        }
      }
      ... on AtsRemoteDepartmentsInsufficientScopeError {
        scope
      }
      ... on AtsRemoteDepartmentsNotConnectedError {
        errorMessage
      }
      ... on AtsRemoteDepartmentsAccessDeniedError {
        errorMessage
      }
      ... on UnknownError {
        errorMessage
      }
    }
  }

  query AtsRemoteOffices($input: AtsRemoteListsInput) {
    atsRemoteOffices(input: $input) {
      __typename
      ... on AtsRemoteOfficesSuccess {
        items {
          id
          name
          parentId
          location
        }
      }
      ... on AtsRemoteOfficesInsufficientScopeError {
        scope
      }
      ... on AtsRemoteOfficesNotConnectedError {
        errorMessage
      }
      ... on AtsRemoteOfficesAccessDeniedError {
        errorMessage
      }
      ... on UnknownError {
        errorMessage
      }
    }
  }

  query AtsRemoteJobs($input: AtsRemoteJobsInput!) {
    atsRemoteJobs(input: $input) {
      __typename
      ... on AtsRemoteJobsSuccess {
        items {
          id
          name
          status
          departmentId
          officeIds
        }
      }
      ... on AtsRemoteJobsInsufficientScopeError {
        scope
      }
      ... on AtsRemoteJobsNotConnectedError {
        errorMessage
      }
      ... on AtsRemoteJobsAccessDeniedError {
        errorMessage
      }
      ... on UnknownError {
        errorMessage
      }
    }
  }

  mutation AtsJobSyncScopeUpdate($input: AtsJobSyncScopeUpdateInput!) {
    atsJobSyncScopeUpdate(input: $input) {
      __typename
      ... on AtsJobSyncScopeUpdateSuccess {
        scope {
          __typename
          departments {
            ids
          }
          offices {
            ids
          }
          jobs {
            ids
          }
        }
      }
      ... on AtsJobSyncScopeUpdateInvalidSelectionError {
        errorMessage
      }
      ... on AtsJobSyncScopeUpdateNotConnectedError {
        errorMessage
      }
      ... on UnknownError {
        errorMessage
      }
    }
  }
`;

/** A scope strategy that needs a remote option list. */
type RestrictedStrategy = Exclude<JobSyncScopeDraft, { kind: 'ALL_JOBS' }>['kind'];

/** One remote department, office, or job that can be selected. */
type Option = AtsRemoteDepartment | AtsRemoteOffice | AtsRemoteJob;

/** One picker option with the tree metadata used by the department and office rows. */
type TreeOption = JobSyncScopeTreeOption<Option>;

/** Props for the Greenhouse job sync scope panel. */
interface Props {
  /** Disables changes while the containing settings surface is disabled. */
  disabled?: boolean;
  /** Closes the containing setup dialog after Cancel or a successful Save. */
  onClose: () => void;
}

/** Props for the stateful panel after the preference has been parsed. */
interface FormProps extends Props {
  /** True when the stored preference could not be parsed; the form then starts from All jobs. */
  unreadable: boolean;
  /** Parsed organization preference used as the initial draft. */
  initialDraft: JobSyncScopeDraft;
}

/** The fallback draft when the organization has no valid saved job sync scope. */
const ALL_JOBS: JobSyncScopeDraft = { kind: 'ALL_JOBS' };

/** Maps a remote insufficient-scope response to the disabled strategy. */
const getInsufficientStrategy = (output: { __typename?: string } | undefined): RestrictedStrategy | undefined => {
  switch (output?.__typename) {
    case 'AtsRemoteDepartmentsInsufficientScopeError':
      return 'DEPARTMENTS';
    case 'AtsRemoteOfficesInsufficientScopeError':
      return 'OFFICES';
    case 'AtsRemoteJobsInsufficientScopeError':
      return 'JOBS';
    default:
      return undefined;
  }
};

/** Returns the remote options that belong to the selected strategy. */
const optionsForStrategy = (
  kind: RestrictedStrategy,
  departments: AtsRemoteDepartment[],
  offices: AtsRemoteOffice[],
  jobs: AtsRemoteJob[]
): Option[] => {
  switch (kind) {
    case 'DEPARTMENTS':
      return departments;
    case 'OFFICES':
      return offices;
    case 'JOBS':
      return jobs;
  }
};

/** Builds a hierarchy label for a remote option. */
const optionLabel = (option: Option, byId: Map<string, Option>): string => {
  const labels: string[] = [];
  const visited = new Set<string>();
  let current: Option | undefined = option;

  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    labels.unshift(current.name);
    const parentId = 'parentId' in current ? current.parentId : undefined;
    current = parentId ? byId.get(parentId) : undefined;
  }

  return labels.join(' › ');
};

/** Compares two drafts without depending on selection order. */
const draftsEqual = (left: JobSyncScopeDraft, right: JobSyncScopeDraft): boolean => {
  if (left.kind !== right.kind) {
    return false;
  }
  if (left.kind === 'ALL_JOBS' || right.kind === 'ALL_JOBS') {
    return true;
  }
  return [...left.ids].sort().join('\u0000') === [...right.ids].sort().join('\u0000');
};

/** Uses a server error message when it exists and a localized fallback otherwise. */
const getErrorMessage = (value: { errorMessage?: string } | undefined, fallback: string): string =>
  value?.errorMessage || fallback;

/** Renders selected ids after hydration; ids missing from the remote list use the error tint. */
const SelectionChips: FC<{
  ids: string[];
  getLabel: (id: string) => string;
  isMissing: (id: string) => boolean;
  hydrating: boolean;
  onRemove: (id: string) => void;
}> = ({ ids, getLabel, isMissing, hydrating, onRemove }) => {
  if (!ids.length) {
    return null;
  }

  if (hydrating) {
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {ids.map((id) => (
          <Skeleton
            key={id}
            data-testid="greenhouse-job-sync-scope-selection-skeleton"
            variant="rounded"
            width={100}
            height={32}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {ids.map((id) => (
        <Chip
          key={id}
          label={getLabel(id)}
          onDelete={() => onRemove(id)}
          sx={
            isMissing(id)
              ? {
                  backgroundColor: (theme) => theme.palette.error.tint,
                  border: (theme) => `1px solid ${theme.palette.borderState.error}`,
                }
              : undefined
          }
        />
      ))}
    </Stack>
  );
};

/** Provides the active picker, its hydration state, and its selection chips. */
const RestrictedPicker: FC<{
  kind: RestrictedStrategy;
  ids: string[];
  options: Option[];
  loading: boolean;
  hydrating: boolean;
  refreshDisabled: boolean;
  onChange: (ids: string[]) => void;
  onRefresh: () => void;
}> = ({ kind, ids, options, loading, hydrating, refreshDisabled, onChange, onRefresh }) => {
  const { t } = useTranslation();
  const refreshLabel = t(
    'hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.refreshList'
  );
  const byId = useMemo(() => new Map(options.map((option) => [option.id, option])), [options]);
  const treeOptions = useMemo(() => buildJobSyncScopeTree(options), [options]);
  const treeById = useMemo(() => new Map(treeOptions.map((option) => [option.id, option])), [treeOptions]);
  // MUI groups options in list order, so the sort key and the group key must agree. Both use the
  // selection as it was when the list opened; otherwise a newly checked row would jump to the top
  // or open a second "Selected" group. The snapshot is taken on open, so a chip removed while the
  // list was closed leaves the Selected group on the next open.
  const [pinnedIds, setPinnedIds] = useState(ids);
  const sortedOptions = useMemo<TreeOption[]>(() => {
    if (kind !== 'JOBS') {
      return treeOptions;
    }

    const pinned = new Set(pinnedIds);
    return [...treeOptions].sort((left, right) => {
      const leftPinned = pinned.has(left.id);
      const rightPinned = pinned.has(right.id);
      if (leftPinned !== rightPinned) {
        return Number(rightPinned) - Number(leftPinned);
      }
      return optionLabel(left, byId).localeCompare(optionLabel(right, byId), undefined, { sensitivity: 'base' });
    });
  }, [byId, kind, pinnedIds, treeOptions]);
  const values = ids.flatMap((id) => {
    const option = treeById.get(id);
    return option ? [option] : [];
  });
  const labels = {
    DEPARTMENTS: {
      empty: t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.selectDepartments'),
      selected: t(
        'hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.selectedDepartments',
        {
          count: ids.length,
        }
      ),
      getMissingLabel: (id: string) =>
        t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.missingDepartment', {
          id,
        }),
    },
    OFFICES: {
      empty: t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.selectOffices'),
      selected: t(
        'hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.selectedOffices',
        {
          count: ids.length,
        }
      ),
      getMissingLabel: (id: string) =>
        t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.missingOffice', {
          id,
        }),
    },
    JOBS: {
      empty: t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.selectJobs'),
      selected: t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.selectedJobs', {
        count: ids.length,
      }),
      getMissingLabel: (id: string) =>
        t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.missingJob', {
          id,
        }),
    },
  }[kind];

  return (
    <Stack gap={1}>
      <Stack direction="row" alignItems="center" gap={1}>
        <FilterSelect
          options={sortedOptions}
          values={values}
          loading={loading}
          placeholderText={labels.empty}
          getLabel={() => (ids.length ? labels.selected : labels.empty)}
          // Full paths keep a parent search result's subtree visible in the filtered tree.
          getOptionLabel={(option) => optionLabel(option, byId)}
          filterOptions={
            kind === 'JOBS'
              ? undefined
              : (options, state) =>
                  filterJobSyncScopeTree(options, state.inputValue, (option) => optionLabel(option, byId))
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          groupBy={
            kind === 'JOBS'
              ? (option) => (pinnedIds.includes(option.id) ? FilterSelectGroup.SELECTED : FilterSelectGroup.SUGGESTED)
              : undefined
          }
          onOpen={kind === 'JOBS' ? () => setPinnedIds(ids) : undefined}
          // FilterList renders grouped records only when renderGroup is also set. Without it, each
          // group record is treated as an option and every row renders blank.
          renderGroup={kind === 'JOBS' ? defaultRenderGroup : undefined}
          // Checkbox rows match the multi-select filters in the schedule flow (see
          // src/components/FilterSelect/utils renderOption), with the hierarchy label kept.
          renderOption={(props, option) => {
            const label = optionLabel(option, byId);
            return (
              <ListItem {...props} key={option.id} data-depth={option.depth} sx={{ pl: 2 + option.depth * 2 }}>
                <ListItemButton sx={{ width: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1} width="100%">
                    <Checkbox checked={ids.includes(option.id)} />
                    <MaybeTooltip label={option.name} title={label} />
                  </Stack>
                </ListItemButton>
              </ListItem>
            );
          }}
          onChange={(_event, nextValues, reason, details) => {
            if (reason === 'clear') {
              onChange([]);
              return;
            }
            if ((reason === 'selectOption' || reason === 'removeOption') && details?.option) {
              onChange(toggleJobSyncScopeSelection(ids, details.option.id, treeOptions));
              return;
            }
            onChange(nextValues.map((option) => option.id));
          }}
        />
        <Tooltip title={refreshLabel}>
          <span>
            <IconButton aria-label={refreshLabel} disabled={refreshDisabled} onClick={onRefresh} size="small">
              <ResetIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <SelectionChips
        ids={ids}
        hydrating={hydrating}
        getLabel={(id) => {
          const option = byId.get(id);
          return option ? optionLabel(option, byId) : labels.getMissingLabel(id);
        }}
        isMissing={(id) => !byId.has(id)}
        onRemove={(id) => onChange(toggleJobSyncScopeSelection(ids, id, treeOptions))}
      />
    </Stack>
  );
};

/** Shows and saves the Greenhouse job sync scope after the preference boundary is parsed. */
const GreenhouseJobSyncScopePanelForm: FC<FormProps> = ({ disabled = false, onClose, initialDraft, unreadable }) => {
  const { t } = useTranslation();
  const greenhouseDepartmentsEnabled = useFeatureFlagBool('org_greenhouse_job_sync_scope_departments');
  const { enqueueSnackbar } = useSnackbar();
  const [draft, setDraft] = useState<JobSyncScopeDraft>(initialDraft);
  const [updateScope, { loading: saving }] = useAtsJobSyncScopeUpdateMutation({
    // The panel and the settings row read the pref from the OrgPrefs query. Refetch it after a save
    // so the next open shows the stored scope instead of the cached one.
    refetchQueries: ['OrgPrefs'],
    awaitRefetchQueries: true,
  });

  const handleRemoteError = useCallback(
    (message: string | undefined) => {
      if (message) {
        enqueueSnackbar(message, { variant: 'error' });
      }
    },
    [enqueueSnackbar]
  );

  const {
    data: departmentsData,
    loading: departmentsLoading,
    refetch: refetchDepartments,
  } = useAtsRemoteDepartmentsQuery({
    skip: draft.kind !== 'DEPARTMENTS',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ atsRemoteDepartments }) => {
      if (
        atsRemoteDepartments.__typename === 'AtsRemoteDepartmentsAccessDeniedError' ||
        atsRemoteDepartments.__typename === 'UnknownError'
      ) {
        handleRemoteError(atsRemoteDepartments.errorMessage);
      }
    },
  });
  const {
    data: officesData,
    loading: officesLoading,
    refetch: refetchOffices,
  } = useAtsRemoteOfficesQuery({
    skip: draft.kind !== 'OFFICES',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ atsRemoteOffices }) => {
      if (
        atsRemoteOffices.__typename === 'AtsRemoteOfficesAccessDeniedError' ||
        atsRemoteOffices.__typename === 'UnknownError'
      ) {
        handleRemoteError(atsRemoteOffices.errorMessage);
      }
    },
  });
  const {
    data: openJobsData,
    loading: openJobsLoading,
    refetch: refetchOpenJobs,
  } = useAtsRemoteJobsQuery({
    variables: { input: {} },
    // Use separate HTTP requests so hydration does not wait for the open-jobs listing.
    context: { batch: false },
    skip: draft.kind !== 'JOBS',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ atsRemoteJobs }) => {
      if (
        atsRemoteJobs.__typename === 'AtsRemoteJobsAccessDeniedError' ||
        atsRemoteJobs.__typename === 'UnknownError'
      ) {
        handleRemoteError(atsRemoteJobs.errorMessage);
      }
    },
  });
  const selectedJobIds = draft.kind === 'JOBS' ? draft.ids : [];
  const { data: selectedJobsData, loading: selectedJobsLoading } = useAtsRemoteJobsQuery({
    variables: { input: { ids: selectedJobIds } },
    // Use separate HTTP requests so hydration does not wait for the open-jobs listing.
    context: { batch: false },
    skip: draft.kind !== 'JOBS' || selectedJobIds.length === 0,
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ atsRemoteJobs }) => {
      if (
        atsRemoteJobs.__typename === 'AtsRemoteJobsAccessDeniedError' ||
        atsRemoteJobs.__typename === 'UnknownError'
      ) {
        handleRemoteError(atsRemoteJobs.errorMessage);
      }
    },
  });

  const departments =
    departmentsData?.atsRemoteDepartments.__typename === 'AtsRemoteDepartmentsSuccess'
      ? departmentsData.atsRemoteDepartments.items
      : [];
  const offices =
    officesData?.atsRemoteOffices.__typename === 'AtsRemoteOfficesSuccess' ? officesData.atsRemoteOffices.items : [];
  const openJobs =
    openJobsData?.atsRemoteJobs.__typename === 'AtsRemoteJobsSuccess' ? openJobsData.atsRemoteJobs.items : [];
  const selectedJobs =
    selectedJobsData?.atsRemoteJobs.__typename === 'AtsRemoteJobsSuccess' ? selectedJobsData.atsRemoteJobs.items : [];
  const jobs = uniqBy([...selectedJobs, ...openJobs], 'id');
  const activeOptions = draft.kind === 'ALL_JOBS' ? [] : optionsForStrategy(draft.kind, departments, offices, jobs);
  const listLoading =
    draft.kind === 'DEPARTMENTS'
      ? departmentsLoading
      : draft.kind === 'OFFICES'
        ? officesLoading
        : draft.kind === 'JOBS'
          ? openJobsLoading
          : false;
  const hydrating =
    draft.kind === 'DEPARTMENTS'
      ? departmentsLoading
      : draft.kind === 'OFFICES'
        ? officesLoading
        : draft.kind === 'JOBS'
          ? selectedJobsLoading
          : false;
  const activeLoading = listLoading || hydrating;
  const insufficientScope =
    draft.kind === 'DEPARTMENTS'
      ? departmentsData?.atsRemoteDepartments.__typename === 'AtsRemoteDepartmentsInsufficientScopeError'
        ? departmentsData.atsRemoteDepartments
        : undefined
      : draft.kind === 'OFFICES'
        ? officesData?.atsRemoteOffices.__typename === 'AtsRemoteOfficesInsufficientScopeError'
          ? officesData.atsRemoteOffices
          : undefined
        : draft.kind === 'JOBS'
          ? openJobsData?.atsRemoteJobs.__typename === 'AtsRemoteJobsInsufficientScopeError'
            ? openJobsData.atsRemoteJobs
            : undefined
          : undefined;
  const insufficientStrategy = getInsufficientStrategy(insufficientScope);
  const notConnected =
    (draft.kind === 'DEPARTMENTS' &&
      departmentsData?.atsRemoteDepartments.__typename === 'AtsRemoteDepartmentsNotConnectedError') ||
    (draft.kind === 'OFFICES' && officesData?.atsRemoteOffices.__typename === 'AtsRemoteOfficesNotConnectedError') ||
    (draft.kind === 'JOBS' && openJobsData?.atsRemoteJobs.__typename === 'AtsRemoteJobsNotConnectedError');
  const formDisabled = disabled || saving || activeLoading || Boolean(notConnected);
  const saveDisabled =
    disabled ||
    saving ||
    hydrating ||
    Boolean(notConnected) ||
    (!unreadable && draftsEqual(draft, initialDraft)) ||
    (draft.kind !== 'ALL_JOBS' && draft.ids.length === 0);

  const refresh = useCallback(() => {
    switch (draft.kind) {
      case 'DEPARTMENTS':
        void refetchDepartments({ input: { refresh: true } });
        return;
      case 'OFFICES':
        void refetchOffices({ input: { refresh: true } });
        return;
      case 'JOBS':
        void refetchOpenJobs({ input: { refresh: true } });
        return;
      case 'ALL_JOBS':
        return;
    }
  }, [draft.kind, refetchDepartments, refetchOffices, refetchOpenJobs]);

  const scopeOptions: Array<{ kind: JobSyncScopeDraft['kind']; label: string }> = [
    {
      kind: 'ALL_JOBS',
      label: t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.allJobs'),
    },
    {
      kind: 'DEPARTMENTS',
      label: t(
        'hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.selectedDepartmentsOption'
      ),
    },
    {
      kind: 'OFFICES',
      label: t(
        'hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.selectedOfficesOption'
      ),
    },
    {
      kind: 'JOBS',
      label: t(
        'hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.selectedJobsOption'
      ),
    },
  ];
  // Keep a saved department scope visible when the rollout flag is off so an existing setting stays editable.
  const visibleScopeOptions = scopeOptions.filter(
    (option) => option.kind !== 'DEPARTMENTS' || greenhouseDepartmentsEnabled || initialDraft.kind === 'DEPARTMENTS'
  );

  const save = async () => {
    const input =
      draft.kind === 'ALL_JOBS'
        ? { allJobs: true }
        : draft.kind === 'DEPARTMENTS'
          ? { departmentIds: draft.ids }
          : draft.kind === 'OFFICES'
            ? { officeIds: draft.ids }
            : { jobIds: draft.ids };
    const result = await updateScope({ variables: { input } });
    const output = result.data?.atsJobSyncScopeUpdate;
    if (!output) {
      enqueueSnackbar(
        t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.saveFailed'),
        {
          variant: 'error',
        }
      );
      return;
    }
    switch (output.__typename) {
      case 'AtsJobSyncScopeUpdateSuccess':
        onClose();
        return;
      case 'AtsJobSyncScopeUpdateInvalidSelectionError':
      case 'AtsJobSyncScopeUpdateNotConnectedError':
      case 'UnknownError':
        enqueueSnackbar(
          getErrorMessage(
            output,
            t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.saveFailed')
          ),
          { variant: 'error' }
        );
        return;
    }
  };

  return (
    <Stack gap={2}>
      {unreadable && (
        <Alert severity="warning">
          {t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.unreadable')}
        </Alert>
      )}
      <FormControl disabled={formDisabled}>
        <RadioGroup
          value={draft.kind}
          onChange={(event) => {
            const nextKind = visibleScopeOptions.find((scopeOption) => scopeOption.kind === event.target.value)?.kind;
            if (nextKind) {
              setDraft(
                nextKind === initialDraft.kind
                  ? initialDraft
                  : nextKind === 'ALL_JOBS'
                    ? ALL_JOBS
                    : { kind: nextKind, ids: [] }
              );
            }
          }}
        >
          {visibleScopeOptions.map((option) => {
            const isInsufficient = option.kind === insufficientStrategy;
            const label = <FormControlLabel value={option.kind} control={<Radio />} label={option.label} />;
            return isInsufficient ? (
              <Tooltip
                key={option.kind}
                title={t(
                  'hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.insufficientScope',
                  {
                    scope: insufficientScope?.scope,
                  }
                )}
              >
                <span>{React.cloneElement(label, { disabled: true })}</span>
              </Tooltip>
            ) : (
              React.cloneElement(label, { key: option.kind })
            );
          })}
        </RadioGroup>
      </FormControl>
      {notConnected && (
        <Alert severity="error">
          {t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.notConnected')}
        </Alert>
      )}
      {draft.kind !== 'ALL_JOBS' && (
        <RestrictedPicker
          kind={draft.kind}
          ids={draft.ids}
          options={activeOptions}
          loading={listLoading}
          hydrating={hydrating}
          refreshDisabled={formDisabled}
          onChange={(ids) => setDraft({ kind: draft.kind, ids })}
          onRefresh={refresh}
        />
      )}
      {draft.kind !== 'ALL_JOBS' && draft.ids.length === 0 && !hydrating && (
        <Typography variant="caption" color="error" role="alert">
          {t('hiring-dashboard-settings-integrations:settings.integrations.greenhouseJobSyncScope.emptySelection')}
        </Typography>
      )}
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button color="inherit" onClick={onClose}>
          {t('hiring-dashboard-common:common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={saveDisabled}
          onClick={save}
          endIcon={saving ? <CircularProgress size={20} /> : undefined}
        >
          {t('hiring-dashboard-common:common.save')}
        </Button>
      </Stack>
    </Stack>
  );
};

/** Reads and parses the preference before mounting the stateful form. */
export const GreenhouseJobSyncScopePanel: FC<Props> = (props) => {
  const [pref] = useOrgPrefJsonTyped<AtsJobSyncScopePref>(JsonOrgPref.ObjAtsJobSyncScope);
  const parsed = parseJobSyncScopeDraft(pref);
  // A stored preference that fails to parse is reported, not hidden: the form starts from All
  // jobs and tells the user that saving replaces the unreadable value.
  const initialDraft = parsed.unwrapOr(ALL_JOBS);

  return (
    <GreenhouseJobSyncScopePanelForm
      key={JSON.stringify(initialDraft)}
      {...props}
      initialDraft={initialDraft}
      unreadable={parsed.isErr()}
    />
  );
};

export default GreenhouseJobSyncScopePanel;
